/**
 * Common honorifics/titles that may precede a given name. Stored lowercase for
 * case-insensitive matching. Keeping this list intentionally short — the goal
 * is to skip the most common leading tokens, not to enumerate every possible
 * title.
 */
const HONORIFICS = new Set([
  'mr',
  'mrs',
  'ms',
  'miss',
  'mx',
  'dr',
  'prof',
  'rev',
  'sir',
  'dame',
  'lord',
  'lady',
  'capt',
  'sgt',
  'lt',
  'col',
  'gen',
  'esq',
]);

/**
 * Returns the first word-like segment of a full name, skipping leading
 * honorifics, using Intl.Segmenter for locale-aware word boundary detection.
 *
 * This is a best-effort approximation. It works well for given-name-first
 * cultures (most of Europe, the Americas, etc.) but will return the family
 * name token in surname-first cultures that use spaces (Vietnamese, Hungarian,
 * romanised Korean/Japanese, etc.) and the first character segment in Chinese
 * or Japanese scripts. There is no standard Intl API to determine name order
 * from a plain string — a proper fix requires explicit firstName/lastName
 * fields from the API.
 *
 * Falls back to the full name when no non-title word-like segment is found
 * (e.g. empty string, all-punctuation, or a bare title like "Dr.").
 */
export function getFirstName(fullName: string): string {
  const segmenter = new Intl.Segmenter(undefined, { granularity: 'word' });
  for (const { segment, isWordLike } of segmenter.segment(fullName)) {
    if (isWordLike && !HONORIFICS.has(segment.toLowerCase())) {
      return segment;
    }
  }
  return fullName;
}

/**
 * Maximum number of participant first names to include in a channel name label
 * before truncating. Used consistently across channel creation and display.
 */
export const PARTICIPANT_NAME_LIST_MAX = 7;

/**
 * Formats a list of names using locale-aware list separators (Intl.ListFormat
 * with type "unit"). Produces "A, B, C" style output — no "and" — which is
 * appropriate for compact labels like channel display names.
 */
export function formatNameList(names: string[]): string {
  return new Intl.ListFormat(undefined, { type: 'unit', style: 'short' }).format(names);
}
